/**
 * @module app.shared
 */

import { CustomElementBase } from 'app/shared/elements/custom-element-base';
import { autoinject, ShadowDOM } from 'aurelia-framework';
import { bindable } from 'aurelia-typed-observable-plugin';
import { I18N } from 'aurelia-i18n';
import { EnhancedElement } from 'app/shared/lib/aurelia';
import { AppLogger } from 'app/shared/lib/logging/app-logger';

@autoinject
export class EssMoreCustomElement extends CustomElementBase {

    ////****************** Fields and Properties ************************************////

    @bindable() protected previewLength: number = 50;
    @bindable() protected showLabel: string = this.i18n.tr('ui:labels.show.more');
    @bindable() protected hideLabel: string = this.i18n.tr('ui:labels.show.less');
    protected fullTextShown: boolean = false;
    protected wrapsMultiline: boolean = false;
    protected fullText: string | null = null;
    protected previewText: string = '';
    protected hiddenText: string | null = null;
    private slot = {} as HTMLElement;
    private originalHTML: string = '';
    private modifiedHTML: string = '';

    ////****************** Lifecycle events (in order of execution) *****************////

    constructor(
        element: Element,
        appLogger: AppLogger,
        private readonly i18n: I18N) {
        super(element, appLogger);
    }

    protected attached() {
        this.slot = (<EnhancedElement>this.element).au.controller.view.slots[ShadowDOM.defaultSlotKey];

        // Check if slot is empty
        if(!this.slot.children.length) {
            return;
        }

        // Check if slot is occupied by an ess-multiline element
        this.wrapsMultiline = this.slot.children[0]?.tagName === 'ESS-MULTILINE';

        if(this.wrapsMultiline) {
            // Preserve slot's original HTML for quickly toggling between preview and full
            this.originalHTML = this.slot.children[0].children[0].innerHTML;
            this.hideLines();
        } else {
            this.hideText();
        }
    }

    ////****************** Functions ************************************************////

    private hideText(): void {
        const text = this.slot.children[0]?.textContent || '';

        // Remove original text node
        this.slot.children[0].remove();

        this.fullText = text;
        if(text.length > this.previewLength
            && this.previewLength >= 0) {
            this.previewText = text.substring(0, this.previewLength);
            this.hiddenText = text.substring(this.previewLength);
        } else {
            this.previewText = text;
        }
    }

    private hideLines(): void {
        // Get lines from ess-multiline component
        const lines: Element[] = Array.from(this.slot.children[0].children[0].children);
        this.fullText = lines.map(line => line.textContent).join('\n'); // rejoin the lines via a newline separator

        // Get total count of characters across all lines
        const charCount = lines.reduce((acc, line) => acc + (line.textContent ? line.textContent.length : 0), 0);
        if(charCount <= this.previewLength) {
            return;
        }

        // Create modified innerHTML dividing preview and hidden text
        let charsRemaining: number = this.previewLength;
        lines.forEach(line => {
            const lineText = line.textContent || '';
            if(charsRemaining >= 0 && lineText.length > charsRemaining) {
                this.previewText = `${lineText.substring(0, charsRemaining)}`;
                this.hiddenText = `${lineText.substring(charsRemaining)}`;

                const hiddenTextSpan = document.createElement('span');
                hiddenTextSpan.classList.add('ess-more', 'ess-show-less');
                hiddenTextSpan.textContent = this.hiddenText;

                const splitSpan = document.createElement('span');
                splitSpan.classList.add('ess-more', 'ess-show-split');
                splitSpan.append(this.previewText);
                splitSpan.appendChild(hiddenTextSpan);

                line.innerHTML = '';
                line.append(splitSpan);
            } else if(lineText.length > charsRemaining) {
                line.classList.add('ess-more', 'ess-show-less');
            }
            charsRemaining -= lineText.length;
        });

        // Store modified HTML for quick toggling
        this.modifiedHTML = this.slot.children[0].children[0].innerHTML;
    }

    protected toggleFullText(): void {
        this.fullTextShown = !this.fullTextShown;
        if(this.wrapsMultiline) {
            this.slot.children[0].children[0].innerHTML = this.fullTextShown ? this.originalHTML : this.modifiedHTML;
        }
    }
}
